import { parkLines } from "./park";
import { streetLines } from "./street";
import { crouch, E, flick, graffitiLine, hold, ride, roll, tap, W, type DemoLine } from "./script";
import { themeParkLines } from "./theme-park";

// park-v2's demo lines (#215, Sunny Beach v2), played in this order
// (`DEMO_LINES` in `demo.ts`). The map keeps Sunny Beach where it was and
// brings Theme Park's coaster station and Street Spot's office steps over,
// moved by `STATION` and `OFFICE`, so their lines carry over unchanged but
// for where they start.

/**
 * Where Theme Park's coaster station went (#221): out on the beachfront,
 * turned a quarter so its deck runs south and the drop heads out over the
 * pier. Its deck's middle `from` in the Theme Park is `to` here.
 */
const STATION = { from: { x: 23.5, z: -19 }, to: { x: 42, z: 42 } };

/**
 * Street Spot's office steps grew into the park's entrance from downtown
 * (#236), raised for #241: thirteen steps off a 3.9 m deck, 13 m wide with
 * two handrails and the concrete hubba down their west side. Street Spot's lines for its six-stair
 * carry over, started where each piece is now, as far back up the deck.
 */
const OFFICE: Record<string, { x: number; z: number }> = {
  "street-six": { x: -45.1, z: -12 },
  "street-rail": { x: -45.33, z: -12 },
  "street-hubba": { x: -48.6, z: -12 },
};

/**
 * The hubba stands higher here than on Street Spot (#241: 1.25 m over the
 * deck's edge), so its line starts a metre further back up the deck and
 * crouches longer for a charged pop onto it (its grind tap still lands
 * from 0.05 s to 0.3 s after the pop).
 */
const popped = (l: DemoLine): DemoLine =>
  l.id !== "street-hubba" ? l : { ...l, start: { ...l.start, z: l.start.z - 1 }, segments: [roll(0.1), crouch(0.5), roll(0.1), ...l.segments.slice(3)] };

/** Sunny Beach's lines that don't carry over: its graffiti moved, and v2 has no cart (#247). */
const V2_DROPS = ["park-graffiti", "park-skitch"];

/** Theme Park's coaster lines. */
const COASTER_IDS = ["theme-coaster", "theme-coaster-ride", "theme-smith", "theme-lift"];

/**
 * A line turned a quarter (east becomes south) round the station's deck and
 * moved with it. Off the bottom of the drop there's no ramp ahead to steer
 * clear of, just the pier, so the drop lines fly straight on and land on it.
 */
const stationed = (l: DemoLine): DemoLine => {
  const dx = l.start.x - STATION.from.x;
  const dz = l.start.z - STATION.from.z;
  const segments = l.segments.map((g) => (g.dur === 0.6 && g.x === 0.5 ? roll(0.6) : g));
  return { ...moved(l, 0, 0), segments, start: { ...l.start, x: STATION.to.x - dz, z: STATION.to.z + dx, yaw: l.start.yaw - Math.PI / 2 } };
};

const moved = (l: DemoLine, dx: number, dz: number): DemoLine => ({
  ...l,
  id: `v2-${l.id}`,
  mapId: "park-v2",
  start: { ...l.start, x: l.start.x + dx, z: l.start.z + dz },
});

/** The seawall line ran out onto the sand and stopped there; here the boardwalk goes on, so it brakes. */
const braked = (l: DemoLine): DemoLine => {
  if (l.id === "park-seawall" || l.id === "park-seawall-hop") return { ...l, segments: [...l.segments.slice(0, -1), roll(0.4), { dur: 2.5, x: 0, y: -1 }] };
  // Off the end of the coaster the promenade runs on onto the boardwalk (#233), its stands and its wall: no turn off the line (there's no rail ahead here), just brake on the way.
  if (l.id === "theme-coaster-ride") return { ...l, segments: [...l.segments.slice(0, -3), roll(0.75), { dur: 4, x: 0, y: -1 }] };
  return l;
};

/**
 * Built on call, not at import, like every map's lines (#229): top-level
 * `.filter`/`.map` calls and `...` spreads are code Rollup can't drop, so
 * they'd ship every park's lines in the hill site.
 */
export const parkV2Lines = (): DemoLine[] => [
  // Sunny Beach's lines that still apply (the graffiti moved: see below;
  // v2 has no beach buggy to skitch, #247).
  ...parkLines().filter((l) => !V2_DROPS.includes(l.id)).map((l) => moved(braked(l), 0, 0)),
  ...themeParkLines().filter((l) => COASTER_IDS.includes(l.id)).map((l) => stationed(braked(l))),
  ...streetLines().flatMap((l) => (OFFICE[l.id] ? [moved(popped(l), OFFICE[l.id]!.x, OFFICE[l.id]!.z)] : [])),
  {
    id: "v2-beachfront",
    name: "Bench Hopper: bench, picnic table, bench along the beachfront",
    mapId: "park-v2",
    // The beachfront's benches and tables stand end to end facing the sea
    // (#238), a bench, a table a step up, a bench a step down, with real gaps
    // between (round 2: 4 m up onto a table, 5.5 m down off one). From the
    // west onto the first bench, ollie off its end up onto the table top,
    // ollie down onto the next bench (Bench Hopper), pop off it onto the next
    // table's top, ollie off that and brake.
    start: { x: -49, z: 42.6, yaw: 1.57, speed: 9 },
    segments: [
      crouch(0.2),
      roll(0.02),
      tap("grind"),
      ride(0.5),
      crouch(0.25),
      roll(0.22),
      tap("grind"),
      ride(0.35),
      crouch(0.25),
      roll(0.22),
      tap("grind"),
      ride(0.55),
      crouch(0.25),
      roll(0.17),
      tap("grind"),
      ride(0.3),
      crouch(0.15),
      roll(0.42),
      { dur: 1.5, x: 0, y: -1 },
    ],
    lands: ["50-50", "Bench Hopper"],
  },
  {
    id: "v2-pier-benches",
    name: "Boardwalk Hop: bench, bench, rail along the boardwalk",
    mapId: "park-v2",
    // The boardwalk's two benches and its flat rail stand end to end facing
    // the sea (#244), the benches at the promenade's size over the deck:
    // east onto the first bench, ollie off its end onto the second, ollie
    // off that down onto the rail (Boardwalk Hop), and off its end.
    start: { x: -5, z: 58.65, yaw: E, speed: 9 },
    segments: [
      crouch(0.2),
      roll(0.02),
      tap("grind"),
      ride(0.6),
      crouch(0.2),
      roll(0.25),
      tap("grind"),
      ride(0.4),
      crouch(0.2),
      roll(0.25),
      tap("grind"),
      ride(0.5),
      crouch(0.2),
      roll(0.6),
      { dur: 2, x: 0, y: -1 },
    ],
    lands: ["50-50", "Boardwalk Hop"],
  },
  {
    id: "v2-boardwalk",
    name: "Grind the gift shop's counter and the burger stand's along the boardwalk",
    mapId: "park-v2",
    // Out on the boardwalk (#233), heading west along the stands' fronts:
    // onto the gift shop's counter, off its end, onto the burger stand's
    // counter, then up the bank at the boardwalk's end and back down.
    start: { x: 17, z: 63.3, yaw: W, speed: 9 },
    segments: [crouch(0.25), roll(0.02), tap("grind"), ride(1.2), crouch(0.25), roll(0.02), tap("grind"), ride(2.6), { dur: 2, x: 0, y: -1 }],
    lands: ["50-50"],
  },
  {
    id: "v2-crosswalk",
    name: "Kickflip the crosswalk off the concrete pyramid, then a Method on the bank",
    mapId: "park-v2",
    // The street in the north-east corner (#240): east along the road, up the
    // pyramid's near side and a Kickflip off its top over the slope and the
    // zebra crossing to the road past it, on to the concrete bank against the
    // shopfronts for a Method, and back west over the pyramid.
    start: { x: 19, z: -48.5, yaw: E, speed: 9 },
    segments: [roll(0.6), crouch(0.4), roll(0.02), tap("flip", -1), roll(2.8), tap("grab"), ride(0.9), roll(2.8), { dur: 2, x: 0, y: -1 }],
    lands: ["Kickflip", "Crosswalk Gap", "Method"],
  },
  {
    id: "v2-combo",
    name: "C-O-M-B-O down the street: Kickflip the crosswalk into a manual, Heelflip out onto the bank",
    mapId: "park-v2",
    // The street's letters (#240) in one combo: C and O over the pyramid and
    // the crossing, landed into a manual for M and B down the road, then a
    // Heelflip out of it at the bank's foot for the last O, landed on the bank.
    start: { x: 19, z: -48.5, yaw: E, speed: 9 },
    segments: [roll(0.6), crouch(0.4), roll(0.02), tap("flip", -1), roll(0.65), ...flick([0, 1], [0, -1]), ride(1.1), crouch(0.15), roll(0.02), tap("flip", 1), roll(1.5), { dur: 2, x: 0, y: -1 }],
    lands: ["Kickflip", "Crosswalk Gap", "Manual", "Heelflip"],
  },
  {
    id: "v2-tape",
    name: "Wallride the coffee shop's window for the Secret Tape, wallie onto the planter",
    mapId: "park-v2",
    // The tape hangs on the coffee shop's window (#240): along the sidewalk
    // angled into the shopfront, grind held up onto it and along the glass,
    // then ollie off it, a Wallie, onto the planter's top and off its end.
    start: { x: 24, z: -52.6, yaw: E + 0.44, speed: 9 },
    segments: [crouch(0.3), roll(0.02), hold("grind", 1.2), roll(0.02), tap("ollie"), roll(1.5), { dur: 2, x: 0, y: -1 }],
    lands: ["Wallride", "Wallie"],
  },
  graffitiLine(
    "park-v2",
    {
      face: W,
      stands: [
        [-53.8, -38],
        [-53.8, -33.5],
        [-53.8, -29],
      ],
    },
    "Power wash the graffiti off the west fence",
  ),
];
